<template>
  <div aria-labelledby="recent-library-folders" class="recent-library">
    <h3 id="recent-library-folders">Library</h3>
    <p class="loading-notice text-muted" v-if="loading"><i class="fa fa-circle-o-notch fa-spin"/>&nbsp;Loading recent library folders...</p>
    <div
      class="alert alert-danger"
      role="alert"
      v-if="error">Error loading library folders. Please refresh the page and try again.</div>
    <ul class="folder-list">
      <li
        class="folder-item"
        v-for="(student, idx) in recentStudents"
        :key="student.uuid">
        <a
          class="folder-name"
          :href="libraryFolderUrl(student, idx)"
          :aria-label="`Library folder: ${student.name}`">
          <i
            v-if="!(student.contributors && student.contributors.length > 0)"
            class="fa fa-folder"
            aria-hidden="true"/>
          <img
            v-else
            src="@/assets/folder_shared.svg"
            alt=""
            class="shared-folder-icon"
            aria-hidden="true">
          <span class="folder-text">{{ truncate(student.name) }}</span>
        </a>
        <span class="folder-time">Updated {{ getRelativeTime(student.updated_at) }}</span>
      </li>
    </ul>
    <a v-if="recentStudents.length" :href="`${this.home}/v/library/all`">View all folders</a>
    <p v-if="!recentStudents.length" class="empty-library">You don't have any folders in your library.</p>
    <a v-if="!recentStudents.length" :href="`${this.home}/v/library/all`"><button class="new-folder-btn">Create new folder</button></a>
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex'
import { formatDistance, parseISO } from 'date-fns'
import { toolkitUrls } from '@/links'

export default {
  name: 'RecentLibraryFolders',
  data() {
    return {
      loading: false,
      error: false,
      ...toolkitUrls,
    }
  },
  computed: {
    ...mapState('user', {
      userUuid: state => state.user.uuid
    }),
    ...mapState('library', {
      recentStudents: state =>  {
        return state.recentStudents.length > 3 ? state.recentStudents.slice(0, -1) : state.recentStudents
      }
    }),
    ...mapGetters('visibility', ['betaVisibility'])
  },
  watch: {
    userUuid(newVal, _) {
      if (newVal) {
        this.loading = true;
        this.loadRecentStudents()
      }
    }
  },
  mounted() {
    if (this.userUuid) {
      this.loading = true;
      this.loadRecentStudents()
    }
  },
  methods: {
    ...mapActions('library', ['fetchRecentStudents']),
    async loadRecentStudents() {
      try {
        await this.fetchRecentStudents({ userId: this.userUuid })
        this.loading = false
      } catch (err) {
          console.error(`Error fetching recent students: ${err}`)
          this.loading = false
          this.error = true
      }
    },
    getRelativeTime(timestamp) {
      const parsedDate = parseISO(timestamp);
      return formatDistance(parsedDate, new Date(), {addSuffix: true})
    },
    truncate(str) {
      if (str.length < 34) {
        return str
      }
      return str.substring(0, 34) + '...'
    },
    libraryFolderUrl(student, idx) {
      const params = new URLSearchParams()
      params.append('ref', 'home')
      params.append('updatedAt', student.updated_at)
      params.append('position', idx + 1)
      if (this.betaVisibility) {
        params.append('beta', 'true')
      }
      return this.libraryFolder(student.uuid) + '?' + params.toString()
    }
  },
};
</script>

<style lang="less" scoped>
@import '~@/variables.less';
  h3 {
      margin-bottom: 1rem;
      color: @header-text-dark;
  }
  .fa-folder {
      color: @folder-gray;
  }
  .folder-list {
    list-style: none;
    padding: 0 0 0 4px;
    margin: 0;
    width: 65%;
  }
  .folder-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.5rem;
  }
  .folder-name {
    display: flex;
    align-items: center;
    gap: 10px;
    text-decoration: none;
  }
  .folder-name:hover .folder-text {
    text-decoration: underline;
  }
  .folder-time {
    font-size: 0.8rem;
    font-style: italic;
    text-align: right;
    align-self: center;
  }
  .recent-library {
    margin-bottom: 2rem;
  }
  a:focus {
    outline: 2px solid lighten(@link-color, 20%);;
    outline-offset: 3px;
  }
  .empty-library {
    font-style: italic;
    margin-top: 2rem;
  }
  .new-folder-btn {
    background-color: #fff;
    color: @gb_blue_medium;
    border: 2px solid @gb_blue_medium;
    border-radius: 6px;
    padding: 4px 12px;
  }

  @media (max-width: @screen-md) {
      .folder-time {
          display: none;
      }
      .folder-name {
          width: 100%;
      }
  }

  .shared-folder-icon {
    width: 32px;
    height: 32px;
    position: relative;
  }
  .fa-folder {
    font-size: 32px;
  }
  .error-message {
    margin: 1rem 0;
    padding: 0.75rem 1rem;
    border-radius: 4px;
    max-width: 65%;
  }
</style>
